import {
  supportTicketFilterData,
  SupportTicketFilterNames,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useSupportTicketQueryScope } from "@redotech/redo-merchant-app-common/support/use-support-ticket-query-scope";
import {
  type DateFilterData,
  DateFilterOperator,
  KnownDateFilterTimeFrame,
} from "@redotech/redo-model/views/advanced-filters/date-filter";
import { FilterOperator } from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import type { FilterGroup } from "@redotech/redo-model/views/views";
import { encodeSupportTicketCountFilterGroup } from "@redotech/redo-support-v2/query/support-ticket-count-query";
import { supportTicketCountsScopedQueryKey } from "@redotech/redo-support-v2/query/support-ticket-query-keys";
import { useQuery } from "@tanstack/react-query";
import { Effect } from "effect";
import { useEffect, useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";
import {
  type DateWindow,
  getMonthToDateWindows,
  isVolumeSurge,
  isWithinVolumePersistWindow,
} from "./volume-trigger";

/** Volume is a coarse month-over-month signal — refetch at most hourly. */
const VOLUME_QUERY_STALE_MS = 60 * 60 * 1000;

const RECENT_VIEW_KEY = "talentpop-recent";
const PRIOR_VIEW_KEY = "talentpop-prior";

/** Per-team localStorage key holding the ISO instant the live surge last fired. */
function volumeTriggeredAtKey(teamId: string | null | undefined): string {
  return `redo.talentpop-volume-triggered-at.${teamId ?? "anon"}`;
}

/** A filter group matching every ticket created within `window` (inclusive). */
function buildCreatedAtFilterGroup(window: DateWindow): FilterGroup {
  const createdAtFilter: DateFilterData = {
    ...supportTicketFilterData[SupportTicketFilterNames.CREATED_AT],
    value: KnownDateFilterTimeFrame.CUSTOM,
    operator: DateFilterOperator.WITHIN,
    customDate: [
      new Date(window.start.year, window.start.month - 1, window.start.day),
      new Date(
        window.end.year,
        window.end.month - 1,
        window.end.day,
        23,
        59,
        59,
        999,
      ),
    ],
  };
  return { operator: FilterOperator.AND, clauses: [createdAtFilter] };
}

/**
 * Fires the TalentPop volume trigger when month-to-date ticket volume is a
 * surge (≥50%) over the same span of days last month. Counts every ticket
 * created in each window regardless of status.
 *
 * Once the live surge fires, the trigger stays on for 24 hours (persisted
 * to localStorage) so the button doesn't flicker day to day. The stamp is
 * only written when no existing window is still active, so re-mounting in
 * the middle of an ongoing window doesn't reset its expiry.
 *
 * RPC failures are logged and treated as "no surge" — the trigger is
 * non-critical UI surfacing and must not show error toasts to users.
 */
export function useTalentPopVolumeTrigger(): boolean {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const queryScope = useSupportTicketQueryScope();

  // `today` is a stable string within a calendar day; recomputing each render
  // lets React Query naturally re-key when a re-render happens to land on a
  // new day (focus/refetch/other state change). It is NOT a guarantee that
  // the key flips at midnight on an idle tab.
  const today = Temporal.Now.plainDateISO().toString();
  const views = useMemo(() => {
    const windows = getMonthToDateWindows(Temporal.PlainDate.from(today));
    return [
      {
        viewKey: RECENT_VIEW_KEY,
        filterGroup: encodeSupportTicketCountFilterGroup(
          buildCreatedAtFilterGroup(windows.recent),
        ),
      },
      {
        viewKey: PRIOR_VIEW_KEY,
        filterGroup: encodeSupportTicketCountFilterGroup(
          buildCreatedAtFilterGroup(windows.prior),
        ),
      },
    ];
  }, [today]);

  const { data } = useQuery({
    queryKey: [
      ...supportTicketCountsScopedQueryKey(queryScope),
      "talentpop-volume-trigger",
      today,
    ],
    queryFn: async () => {
      const client = effectApi.getClient();
      return await effectApi.runPromise(
        client.supportTicketCounts
          .getSupportTicketCounts({ payload: { views } })
          .pipe(
            Effect.tapError((error) =>
              Effect.sync(() => {
                console.warn(
                  "[TalentPop] volume trigger query failed; skipping surge check",
                  error,
                );
              }),
            ),
            Effect.catchAll(() => Effect.succeed<Record<string, number>>({})),
          ),
      );
    },
    enabled: !!queryScope.teamId,
    staleTime: VOLUME_QUERY_STALE_MS,
  });

  const liveSurge = useMemo(() => {
    if (!data) {
      return false;
    }
    return isVolumeSurge(data[RECENT_VIEW_KEY] ?? 0, data[PRIOR_VIEW_KEY] ?? 0);
  }, [data]);

  const [triggeredAtRaw, setTriggeredAtRaw] = useLocalStorage<string | null>(
    volumeTriggeredAtKey(queryScope.teamId),
    null,
  );

  // Stamp the 24-hour persistence window when the live check is surging AND
  // there is no still-active stamp. Skipping the write when a window is
  // already open prevents fresh page loads from resetting the expiry.
  useEffect(() => {
    if (!liveSurge) {
      return;
    }
    const existing = triggeredAtRaw
      ? Temporal.Instant.from(triggeredAtRaw)
      : null;
    if (isWithinVolumePersistWindow(existing, Temporal.Now.instant())) {
      return;
    }
    setTriggeredAtRaw(Temporal.Now.instant().toString());
  }, [liveSurge, triggeredAtRaw, setTriggeredAtRaw]);

  // Evaluated every render so the persistence window expires on time, even
  // without a state change to trigger a recompute.
  if (liveSurge) {
    return true;
  }
  const triggeredAt = triggeredAtRaw
    ? Temporal.Instant.from(triggeredAtRaw)
    : null;
  return isWithinVolumePersistWindow(triggeredAt, Temporal.Now.instant());
}
